import React, { lazy, Suspense } from 'react'
import { Route, Routes } from 'react-router-dom'
import { ErrorBoundary } from 'react-error-boundary'

import { onError } from '@apollo/client/link/error'
import { ApolloClient, ApolloProvider, InMemoryCache } from '@apollo/client'
import { RetryLink } from '@apollo/client/link/retry'
import { setContext } from '@apollo/client/link/context'
import { createUploadLink } from 'apollo-upload-client'

import { memoize } from 'lodash/fp'
import { Navbar } from '@blueprintjs/core'
import { ToastProvider } from './Hooks/toast'
import AuthRoute from './Components/AuthRoute'
import { getAuthState, useAuth0 } from './Hooks/auth0'
import AuthLoginRoute from './Components/AuthLoginRoute'
import { GoOfflineModal, GoOnlineModal } from './Components'
import { CAN_ADMINISTER_USER, CAN_VIEW_ADMIN_DASHBOARD, CAN_VIEW_INVENTORY, CAN_VIEW_ROLES } from './utils/enums'

import Login from './Components/Login'
import { useOnlineUtils } from './Hooks/offline'
import { useMediaQuery } from 'react-responsive'
import { ErrorFallback, Spinner } from './Elements'
import { DesktopNavbar, MobileNavbar } from './Components/Navbar'

import { logoNav, envShowCentralTexas } from './utils/helpers'

const WeeklyGuniteCalendar = lazy(() => import('./Components/Reports/Calendar/SuperCalendarRoutes/WeeklyGuniteCalendar'))
const DailyGuniteCalendar = lazy(() => import('./Components/Reports/Calendar/SuperCalendarRoutes/DailyGuniteCalendar'))
const WeeklyPebbleCalendar = lazy(() => import('./Components/Reports/Calendar/SuperCalendarRoutes/WeeklyPebbleCalendar'))
const DailyPebbleCalendar = lazy(() => import('./Components/Reports/Calendar/SuperCalendarRoutes/DailyPebbleCalendar'))
const WeeklyWarrantyCalendar = lazy(() => import('./Components/Reports/Calendar/SuperCalendarRoutes/WeeklyWarrantyCalendar'))
const DailyWarrantyCalendar = lazy(() => import('./Components/Reports/Calendar/SuperCalendarRoutes/DailyWarrantyCalendar'))

const MonthlyBoard = lazy(() => import('./Components/DigitalBoard/Warranty/monthlyBoard'))

const DataExport = lazy(() => import('./Components/Admin/DataExport'))

const TeamSchedule = lazy(() => import('./Components/Schedule/TeamSchedule'))
const MySchedule = lazy(() => import('./Components/Schedule/MySchedule'))

const InventoryRelocationOrderNew = lazy(() => import('./Components/InventoryRelocationOrder/InventoryRelocationOrderNew'))
const InventoryRelocationOrderPdf = lazy(() => import('./Components/InventoryRelocationOrder/InventoryRelocationOrderPdf'))

const FieldMap = lazy(() => import('./Components/FieldMap'))
const SuperDetail = lazy(() => import('./Components/SuperDetail'))

const AdminDashboard = lazy(() => import('./Components/Admin'))
const RolePermissions = lazy(() => import('./Components/RolePermissions'))

const Dashboard = lazy(() => import('./Components/Dashboard/index'))

const UserNew = lazy(() => import('./Components/User/UserNew'))
const UserSearch = lazy(() => import('./Components/User/UserSearch'))
const UserEdit = lazy(() => import('./Components/User/UserEdit'))

const JobNew = lazy(() => import('./Components/Job/JobNew'))
const JobEdit = lazy(() => import('./Components/Job/JobEdit'))
const JobSearch = lazy(() => import('./Components/Job/JobSearch'))
const JobMap = lazy(() => import('./Components/Job/JobMap'))

const EstimateNew = lazy(() => import('./Components/Estimate/EstimateNew'))
const EstimateSearch = lazy(() => import('./Components/Estimate/EstimateSearch'))
const EstimateEdit = lazy(() => import('./Components/Estimate/EstimateEdit'))

const CustomerNew = lazy(() => import('./Components/Customer/CustomerNew'))
const CustomerSearch = lazy(() => import('./Components/Customer/CustomerSearch'))
const CustomerEdit = lazy(() => import('./Components/Customer/CustomerEdit'))

const EmployeeNew = lazy(() => import('./Components/Employee/EmployeeNew'))
const EmployeeEdit = lazy(() => import('./Components/Employee/EmployeeEdit'))
const EmployeeSearch = lazy(() => import('./Components/Employee/EmployeeSearch'))

const HomeownerNew = lazy(() => import('./Components/Homeowner/HomeownerNew'))
const HomeownerSearch = lazy(() => import('./Components/Homeowner/HomeownerSearch'))
const HomeownerEdit = lazy(() => import('./Components/Homeowner/HomeownerEdit'))

const DailyYardage = lazy(() => import('./Components/DailyYardage'))
const DailyBatches = lazy(() => import('./Components/DailyBatches'))

const InventoryCount = lazy(() => import('./Components/InventoryCount/index'))
const ColorRecipe = lazy(() => import('./Components/ColorRecipe/index'))

const PresiteSchedule = lazy(() => import('./Components/PresiteSchedule'))

const DigitalBoard = lazy(() => import('./Components/DigitalBoard/export'))
const PebbleWeeklyDigitalBoard = lazy(() => import('./Components/DigitalBoard/PebbleWeekly/export'))
const WarrantyDigitalBoard = lazy(() => import('./Components/DigitalBoard/Warranty/export'))
const DayView = lazy(() => import('./Components/DigitalBoard/DayView/export'))

const Gunite = lazy(() => import('./Components/Card/Gunite'))
const Boulder = lazy(() => import('./Components/Card/Boulder'))
const Pebble = lazy(() => import('./Components/Card/Pebble'))
const Prep = lazy(() => import('./Components/Card/Prep'))

const ContactList = lazy(() => import('./Components/Reports/Customer/ContactList'))
const TopContactList = lazy(() => import('./Components/Reports/Customer/index'))
const Calendar = lazy(() => import('./Components/Reports/Calendar'))
const CoverageReport = lazy(() => import('./Components/Reports/CoverageReport'))
const PerCompanyJob = lazy(() => import('./Components/Reports/PerCompanyJob'))
const SlurryPickupReport = lazy(() => import('./Components/Reports/SlurryPickupReport'))
const WarrantyAging = lazy(() => import('./Components/Reports/WarrantyAging'))
const WarrantyJobsOutstanding = lazy(() => import('./Components/Reports/WarrantyJobsOutstanding'))
const ColorLocation = lazy(() => import('./Components/Reports/ColorLocation'))
const InventoryReport = lazy(() => import('./Components/Reports/Inventory'))
const YearOverYear = lazy(() => import('./Components/Reports/YearOverYear'))

const PurchaseOrderNew = lazy(() => import('./Components/PurchaseOrder/PurchaseOrderNew'))
const PurchaseOrderEdit = lazy(() => import('./Components/PurchaseOrder/PurchaseOrderEdit'))
const PurchaseOrderSearch = lazy(() => import('./Components/PurchaseOrder/PurchaseOrderSearch'))
const PurchaseOrderPdf = lazy(() => import('./Components/PurchaseOrder/PurchaseOrderPdf'))

const WorkOrderNew = lazy(() => import('./Components/WorkOrder/WorkOrderNew'))
const WorkOrderEdit = lazy(() => import('./Components/WorkOrder/WorkOrderEdit'))
const GuniteWorkOrderPdf = lazy(() => import('./Components/WorkOrder/GuniteWorkOrder/GuniteWorkOrderPdf'))
const BoulderWorkOrderPdf = lazy(() => import('./Components/WorkOrder/BoulderWorkOrder/BoulderWorkOrderPdf'))
const PebbleWorkOrderPdf = lazy(() => import('./Components/WorkOrder/PebbleWorkOrder/PebbleWorkOrderPdf/index'))
const PebbleWorkOrderBlankPdf = lazy(() => import('./Components/WorkOrder/PebbleWorkOrder/PebbleWorkOrderPdf/blank'))
const PebbleDailyWorkOrderPdf = lazy(() => import('./Components/WorkOrder/PebbleDailyWorkOrder/PebbleDailyWorkOrderPdf'))
const PrepWorkOrderPdf = lazy(() => import('./Components/WorkOrder/PrepWorkOrder/PrepWorkOrderPdf'))
const WarrantyWorkOrderPdf = lazy(() => import('./Components/WorkOrder/WarrantyWorkOrder/WarrantyWorkOrderPdf'))

const PrintQueueSearch = lazy(() => import('./Components/PrintQueue/PrintQueueSearch'))
const PrintQueueSelectionCardView = lazy(() => import('./Components/PrintQueue/PrintQueueSelectionCardView'))
const PrintQueueSelectionWorkOrderView = lazy(() => import('./Components/PrintQueue/PrintQueueSelectionWorkOrderView'))

const ForemanWorkOrderGuniteEdit = lazy(() => import('./Components/SuperDetail/ForemanWorkOrder/GuniteWorkOrder/GuniteEdit'))
const ForemanWorkOrderBoulderEdit = lazy(() => import('./Components/SuperDetail/ForemanWorkOrder/BoulderWorkOrder/BoulderEdit'))
const ForemanWorkOrderPebbleEdit = lazy(() => import('./Components/SuperDetail/ForemanWorkOrder/PebbleWorkOrder/PebbleEdit'))
const ForemanDailyWorkOrderPebbleEdit = lazy(() => import('./Components/SuperDetail/ForemanWorkOrder/PebbleDailyWorkOrder/PebbleDailyEdit'))
const ForemanPrepWorkOrderPebbleEdit = lazy(() => import('./Components/SuperDetail/ForemanWorkOrder/PrepWorkOrder/PrepEdit'))

const isLocal = window.location.hostname === 'localhost'

const httpLink = createUploadLink({
    uri: isLocal ? 'http://localhost:3001/graphql' : '/v1/graphql'
})

const getToken = memoize(() => {
    const state = getAuthState()

    return state && state.idToken
})

const authLink = setContext((_, { headers }) => {
    const token = getToken()

    return {
        headers: {
            ...headers,
            'Authorization': token ? `Bearer ${token}` : ''
        }
    }
})

const retryLink = new RetryLink({
    attempts: {
        max: 3
    }
})

const excludedOperations = ['Notifications', 'OfflineJobs']

const errorLink = (setNetworkError) => onError(({ operation, graphQLErrors, networkError }) => {
    if (networkError && networkError.toString && networkError.toString().includes('Failed to fetch') && !excludedOperations.includes(operation.operationName)) {
        setNetworkError(networkError)
    }
})

const cache = new InMemoryCache()
let client


const LazyRoute = ({ path = '/', children = <></> }) => <Route
    path={path}
    element={
        <Suspense fallback={<Spinner />}>
            {children}
        </Suspense>
    }
/>

export default () => {
    const { networkError, setNetworkError } = useOnlineUtils()

    if (!client) {
        client = new ApolloClient({
            link: authLink.concat(errorLink(setNetworkError)).concat(retryLink).concat(httpLink),
            cache,
            defaultOptions: {
                watchQuery: {
                    fetchPolicy: 'cache-and-network'
                }
            }
        })
    }

    const { isLoggedIn, isAuthenticated } = useAuth0()
    const isMobile = useMediaQuery({ query: '(max-width: 875px)' })


    if (!isAuthenticated || !isLoggedIn()) {
        return (
            <ToastProvider>
                <Navbar style={{ width: 'auto', zIndex: 20 }}>
                    <Navbar.Group>
                        <Navbar.Heading className="heading">
                            <a href="/">
                                {envShowCentralTexas
                                    ? <img src={logoNav} width='100%' alt='MMG' />
                                    : <object type="image/svg+xml" data={logoNav} alt='MMG' width='55px' height='55px' />
                                }
                            </a>
                            <a className='nav-header' href='/'>Insite</a>
                        </Navbar.Heading>
                    </Navbar.Group>
                </Navbar>

                <Login />
            </ToastProvider>
        )
    }

    return (
        <ToastProvider>
            <ApolloProvider client={client}>
                <div style={{ minWidth: 'fit-content' }}>

                    <GoOfflineModal networkError={networkError} />
                    <GoOnlineModal />

                    {!isMobile && <DesktopNavbar isLoggedIn={isLoggedIn} />}
                    {isMobile && <MobileNavbar isLoggedIn={isLoggedIn} />}

                    <div className='space'>
                        <ErrorBoundary FallbackComponent={ErrorFallback}
                            onError={(error, info) => {

                                console.log(error)
                            }}>
                            <Suspense fallback={<div>You're a quick one, aren't ya?</div>}>
                                <Routes>
                                    <LazyRoute children={<Dashboard />} path='/' />
                                    <LazyRoute children={<FieldMap />} path='/supervisor/map' />

                                    <AuthLoginRoute as={Login} path='/login' />

                                    <AuthRoute as={AdminDashboard} path='/admin' action={CAN_VIEW_ADMIN_DASHBOARD} />
                                    <AuthRoute as={RolePermissions} path='/roles' action={CAN_VIEW_ROLES} />

                                    <LazyRoute children={<EstimateNew />} path='/estimate/new' />
                                    <LazyRoute children={<EstimateSearch />} path='/estimate' />
                                    <LazyRoute children={<EstimateEdit />} path='/estimate/:estimateID/edit' />

                                    <LazyRoute children={<JobSearch />} path='/job' />
                                    <LazyRoute children={<JobNew />} path='job/new' />
                                    <LazyRoute children={<JobEdit />} path='job/:jobID/edit' />
                                    <LazyRoute children={<JobEdit />} path='job/:jobID/edit/:jobDetailID' />

                                    <LazyRoute children={<DailyYardage />} path='/daily-yardage/:jobID' />
                                    <LazyRoute children={<DailyBatches />} path='/daily-batches/:jobID' />
                                    <LazyRoute children={<CustomerSearch />} path='/customer' />

                                    <LazyRoute children={<CustomerNew />} path='/customer/new' />
                                    <LazyRoute children={<CustomerEdit />} path='/customer/:customerID/edit' />
                                    <LazyRoute children={<EmployeeSearch />} path='/employee' />
                                    <LazyRoute children={<EmployeeNew />} path='/employee/new' />
                                    <LazyRoute children={<EmployeeEdit />} path='/employee/:employeeID/edit' />
                                    <LazyRoute children={<HomeownerSearch />} path='/homeowner' />
                                    <LazyRoute children={<HomeownerNew />} path='/homeowner/new' />
                                    <LazyRoute children={<HomeownerEdit />} path='/homeowner/:homeownerID/edit' />

                                    <LazyRoute children={<ColorRecipe />} path='/color-recipe' />

                                    <AuthRoute as={InventoryCount} path='/inventory-count' action={CAN_VIEW_INVENTORY} />
                                    <LazyRoute children={<InventoryReport />}
                                        path='/reports/inventory/:inventory_location/:product_category_id' />
                                    <LazyRoute children={<InventoryRelocationOrderNew />} path='/inventoryRelocation' />
                                    <LazyRoute children={<InventoryRelocationOrderPdf />}
                                        path='/inventoryRelocation/:inventoryRelocationOrderId/pdf' />

                                    <AuthRoute as={UserSearch} path='/user' action={CAN_ADMINISTER_USER} />
                                    <AuthRoute as={UserNew} path='/user/new' action={CAN_ADMINISTER_USER} />
                                    <AuthRoute as={UserEdit} path='/user/:userID/edit' action={CAN_ADMINISTER_USER} />
                                    <AuthRoute as={DataExport} path='/admin/export' action={CAN_ADMINISTER_USER} />

                                    <LazyRoute children={<PrintQueueSearch />} path='/print-queue' />
                                    <LazyRoute children={<PrintQueueSelectionCardView />}
                                        path='/print-queue-selection-card-view/:stage/:selection' />
                                    <LazyRoute children={<PrintQueueSelectionWorkOrderView />}
                                        path='/print-queue-selection-wo-view/:selection' />

                                    <LazyRoute children={<BoulderWorkOrderPdf />}
                                        path='/job/:jobId/:jobDetailId/boulder-work-order/:workOrderId/pdf' />
                                    <LazyRoute children={<PrepWorkOrderPdf />}
                                        path='/job/:jobId/:jobDetailId/prep-work-order/:workOrderId/pdf' />

                                    <LazyRoute children={<Pebble />} path='/job/:jobID/pebble/:jobDetailID' />
                                    <LazyRoute children={<Gunite />} path='/job/:jobID/gunite/:jobDetailID' />
                                    <LazyRoute children={<Boulder />}
                                        path='/job/:jobID/:jobDetailID/boulder/:boulderID' />
                                    <LazyRoute children={<Prep />} path='/job/:jobID/prep/:jobDetailID' />

                                    <LazyRoute children={<DigitalBoard />} path='DigitalBoard/:category' />
                                    <LazyRoute children={<PebbleWeeklyDigitalBoard />} path='PebbleWeeklyDigitalBoard' />
                                    <LazyRoute children={<WarrantyDigitalBoard />} path='DigitalBoard/Warranty' />
                                    <LazyRoute children={<MonthlyBoard />} path='DigitalBoard/WarrantyMonth' />
                                    <LazyRoute children={<MySchedule />} path='Schedule' />
                                    <LazyRoute children={<TeamSchedule />} path='TeamSchedule' />
                                    <LazyRoute children={<DayView />} path='DayView/Gunite' key={'day-view-gunite'} />
                                    <LazyRoute children={<DayView />} path='DayView/Pebble' key={'day-view-pebble'} />
                                    <LazyRoute children={<PresiteSchedule />} path='PresiteSchedule' />

                                    <LazyRoute children={<WorkOrderNew />}
                                        path='/job/:jobId/:jobDetailId/work-order/new/:type' />
                                    <LazyRoute children={<WorkOrderEdit />}
                                        path='/job/:jobId/:jobDetailId/work-order/:type/:workOrderId/edit' />

                                    <LazyRoute children={<GuniteWorkOrderPdf />}
                                        path='/job/:jobId/:jobDetailId/gunite-work-order/:workOrderId/pdf' />
                                    <LazyRoute children={<BoulderWorkOrderPdf />}
                                        path='/job/:jobId/:jobDetailId/boulder-work-order/:workOrderId/pdf' />
                                    <LazyRoute children={<PebbleWorkOrderPdf />}
                                        path='/job/:jobId/:jobDetailId/pebble-work-order/:workOrderId/pdf' />
                                    <LazyRoute children={<PebbleDailyWorkOrderPdf />}
                                        path='/job/:jobId/:jobDetailId/pebble-daily-work-order/:workOrderId/pdf' />
                                    <LazyRoute children={<PebbleWorkOrderBlankPdf />} path='/job/pebble-work-order/pdf' />
                                    <LazyRoute children={<WarrantyWorkOrderPdf />}
                                        path='/job/:jobId/:jobDetailId/warranty-work-order/:workOrderId/pdf' />

                                    <LazyRoute children={<PurchaseOrderNew />} path='/purchaseOrder/new' />
                                    <LazyRoute children={<PurchaseOrderEdit />}
                                        path='/purchaseOrder/:purchaseOrderId/edit' />
                                    <LazyRoute children={<PurchaseOrderSearch />} path='/purchaseOrder' />
                                    <LazyRoute children={<PurchaseOrderPdf />}
                                        path='/purchaseOrder/:purchaseOrderId/pdf' />

                                    <LazyRoute children={<JobMap />} path='/map' />

                                    <LazyRoute children={<Calendar />} path='/calendar/pebble' key={'calendar-report-pebble'} />
                                    <LazyRoute children={<Calendar />} path='/calendar/gunite' key={'calendar-report-gunite'} />


                                    <LazyRoute children={<WeeklyGuniteCalendar />} path='/week/gunite' />
                                    <LazyRoute children={<DailyGuniteCalendar />} path='/day/gunite' />
                                    <LazyRoute children={<WeeklyPebbleCalendar />} path='/week/pebble' />
                                    <LazyRoute children={<DailyPebbleCalendar />} path='/day/pebble' />
                                    <LazyRoute children={<WeeklyWarrantyCalendar />} path='week/warranty' />
                                    <LazyRoute children={<DailyWarrantyCalendar />} path='day/warranty' />

                                    <LazyRoute children={<YearOverYear />} path='/reports/year-over-year/:category' />
                                    <LazyRoute children={<TopContactList />} path='/reports/top-contacts' />
                                    <LazyRoute children={<ContactList />} path='/reports/customer-contact-list' />

                                    <LazyRoute children={<CoverageReport />} path='/reports/coverage' />
                                    <LazyRoute children={<PerCompanyJob />} path='/reports/per-company-job' />
                                    <LazyRoute children={<WarrantyAging />} path='/reports/warranty-aging' />
                                    <LazyRoute children={<SlurryPickupReport />} path='/reports/slurry-pickup' />
                                    <LazyRoute children={<ColorLocation />} path='/reports/color-location' />
                                    <LazyRoute children={<WarrantyJobsOutstanding />}
                                        path='/reports/warranty-jobs-outstanding' />

                                    <LazyRoute children={<SuperDetail />} path='SuperDetail/:jobDetailID' />

                                    <LazyRoute children={<ForemanWorkOrderGuniteEdit />}
                                        path='/SuperDetail/:jobDetailId/job/:jobId/work-order/gunite/:workOrderId/edit' />
                                    <LazyRoute children={<ForemanWorkOrderBoulderEdit />}
                                        path='/SuperDetail/:jobDetailId/job/:jobId/work-order/boulder/:workOrderId/edit' />
                                    <LazyRoute children={<ForemanWorkOrderPebbleEdit />}
                                        path='/SuperDetail/:jobDetailId/job/:jobId/work-order/pebble/:workOrderId/edit' />
                                    <LazyRoute children={<ForemanDailyWorkOrderPebbleEdit />}
                                        path='/SuperDetail/:jobDetailId/job/:jobId/work-order/daily/:workOrderId/edit' />
                                    <LazyRoute children={<ForemanPrepWorkOrderPebbleEdit />}
                                        path='/SuperDetail/:jobDetailId/job/:jobId/work-order/prep/:workOrderId/edit' />
                                </Routes>
                            </Suspense>
                        </ErrorBoundary>
                    </div>
                </div>
            </ApolloProvider>
        </ToastProvider>
    )
}